/**
 * Ojetá vozidla helpers — list filter shape + URL sync.
 *
 * Mirrors the calibrated `vozidla/helpers.ts` pattern so the
 * /ojeta-vozidla list and /ojeta-vozidla/$id detail rail share
 * filter state via URL search-params.
 *
 * Status filter values map to the literal `stav_vozidla` strings the API
 * accepts. Unlike new vehicles, the
 * "Skladový" filter does NOT expand to include "Registrovaný" — the legacy
 * page treats them as separate options.
 */

import { searchParamString } from '@/lib/list-search';
import type { OjetoVozidlo } from './api';

export type StavFilter =
  | 'all'
  | 'skladovy'
  | 'registrovany'
  | 'zakaznicky'
  | 'predany_dealerovi'
  | 'predany_zakaznikovi'
  | 'prevedeno';

export const STAV_VALUE: Record<Exclude<StavFilter, 'all'>, string> = {
  skladovy: 'Skladový',
  registrovany: 'Registrovaný',
  zakaznicky: 'Zákaznický',
  predany_dealerovi: 'Předaný dealerovi',
  predany_zakaznikovi: 'Předaný zákazníkovi',
  prevedeno: 'Převedeno',
};

export const STAV_LABEL: Record<StavFilter, string> = {
  all: 'Všechna',
  skladovy: 'Skladová',
  registrovany: 'Registrovaná',
  zakaznicky: 'Zákaznická',
  predany_dealerovi: 'Předaná dealerovi',
  predany_zakaznikovi: 'Předaná zákazníkovi',
  prevedeno: 'Převedená do jiné firmy',
};

// Derived from the filter map, so a new state can't be added to the menu and forgotten here.
const VALID_STAV: ReadonlySet<StavFilter> = new Set<StavFilter>(['all', ...(Object.keys(STAV_VALUE) as StavFilter[])]);

export const DEFAULT_STAV: StavFilter = 'skladovy';

export interface OjetaSearch {
  stav?: StavFilter;
  brand?: number;
  model?: number;
  search?: string;
}

export function parseOjetaSearch(input: Record<string, unknown>): OjetaSearch {
  const out: OjetaSearch = {};
  if (typeof input.stav === 'string' && VALID_STAV.has(input.stav as StavFilter)) {
    out.stav = input.stav as StavFilter;
  }
  if (input.brand != null) {
    const n = Number(input.brand);
    if (Number.isFinite(n) && n > 0) out.brand = n;
  }
  if (input.model != null) {
    const n = Number(input.model);
    if (Number.isFinite(n) && n > 0) out.model = n;
  }
  const search = searchParamString(input.search);
  if (search) out.search = search;
  return out;
}

/** Build the API filter object from the URL search shape. */
export function ojetaFiltersFromSearch(s: OjetaSearch): {
  znacka_vozu?: string;
  vozidlo_model?: string;
  stav_vozidla?: string;
  search?: string;
} {
  const filters: {
    znacka_vozu?: string;
    vozidlo_model?: string;
    stav_vozidla?: string;
    search?: string;
  } = {};
  if (s.brand != null) filters.znacka_vozu = String(s.brand);
  // Model is brand-scoped, so it only narrows when a brand is also set.
  if (s.brand != null && s.model != null) filters.vozidlo_model = String(s.model);
  const stav: StavFilter = s.stav ?? DEFAULT_STAV;
  if (stav !== 'all') filters.stav_vozidla = STAV_VALUE[stav];
  // Free-text search — the API ORs it across SPZ, VIN, provedení, and model name.
  if (s.search) filters.search = s.search;
  return filters;
}

/** Row badge tone: a transferred car is retired (neutral), a registered one amber, the rest green. */
export function stavBadgeTone(v: { stav_vozidla: string }): 'neutral' | 'warn' | 'ok' {
  if (v.stav_vozidla === 'Převedeno') return 'neutral';
  return v.stav_vozidla === 'Registrovaný' ? 'warn' : 'ok';
}

export function isRegistrovany(v: { stav_vozidla: string }): boolean {
  return v.stav_vozidla === 'Registrovaný';
}

export function stavBadgeLabel(stav: string): string {
  if (stav === 'Registrovaný') return 'Registr.';
  if (stav === 'Skladový') return 'Skladový';
  if (stav === 'Zákaznický') return 'Zákazn.';
  if (stav === 'Předaný zákazníkovi') return 'Předáno';
  if (stav === 'Předaný dealerovi') return 'Dealer';
  if (stav === 'Převedeno') return 'Převedeno';
  return stav;
}

export function vehicleDisplayName(v: Pick<OjetoVozidlo, 'nazev_znacky' | 'nazev_modelu'>): string {
  const brand = v.nazev_znacky?.trim() || '';
  const model = v.nazev_modelu?.trim() || '';
  return [brand, model].filter(Boolean).join(' ');
}

// ─── Termíny (STK · servisní prohlídka) ─────────────────────────────────────
//
// Shared by the detail/list badges and the dashboard card. Mirrors the API's
// services/ojeta-terminy.ts: whole-day arithmetic in UTC so DST never shifts
// a deadline by a day, and anything before 2000 counts as "not set" (legacy
// zero dates).

export type TerminDruh = 'stk' | 'servis';

export const TERMIN_LABEL: Record<TerminDruh, string> = {
  stk: 'STK',
  servis: 'Servisní prohlídka',
};

/** Compact kind labels for one-line row badges ("Servis · −12 d"). */
export const TERMIN_SHORT_LABEL: Record<TerminDruh, string> = {
  stk: 'STK',
  servis: 'Servis',
};

/** How far ahead a deadline starts to warn (matches the dashboard query). */
export const TERMIN_WINDOW_DAYS = 30;

function utcDay(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  if (m[1] < '2000') return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** Whole days from today (local calendar day) to `date`; negative = overdue;
 *  null when the date is empty/invalid. */
/** Merge deadline lists (nové + ojeté) into one: by date, STK before service, then SPZ. */
export function mergeTerminy<T extends { datum: string; druh: 'stk' | 'servis'; spz_vozu: string }>(
  ...lists: Array<T[] | undefined>
): T[] {
  const rank = (d: T['druh']) => (d === 'stk' ? 0 : 1);
  return lists
    .flatMap((l) => l ?? [])
    .sort((a, b) => a.datum.localeCompare(b.datum) || rank(a.druh) - rank(b.druh) || a.spz_vozu.localeCompare(b.spz_vozu));
}

export function deadlineDays(date: string | null | undefined, now: Date = new Date()): number | null {
  if (!date) return null;
  const target = utcDay(date);
  if (target == null) return null;
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target - today) / 86_400_000);
}

export type DeadlineTone = 'danger' | 'warn';

/** danger = overdue, warn = due within the window, null = fine / unknown. */
export function deadlineTone(days: number | null): DeadlineTone | null {
  if (days == null) return null;
  if (days < 0) return 'danger';
  if (days <= TERMIN_WINDOW_DAYS) return 'warn';
  return null;
}

/** Human hint next to a deadline: "po termínu 5 dní" · "dnes" · "za 12 dní". */
export function deadlineHint(days: number): string {
  if (days === 0) return 'dnes';
  const n = Math.abs(days);
  const word = n === 1 ? 'den' : n < 5 ? 'dny' : 'dní';
  return days < 0 ? `po termínu ${n} ${word}` : `za ${n} ${word}`;
}


